import { Stack, Tabs } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import type { EmptyStateButtons } from '@repo/common/components/NewEmptyState/EmptyState';
import { EmptyState } from '@repo/common/components/NewEmptyState/EmptyState';
import TabsList from '@repo/common/components/TabsList';
import { UserRole } from '@repo/common/enums/UserRole';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthUser, useMonitorList } from '../../api';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import { FullWidthLoadingSpinner } from '../../components/LoadingSpinner';
import { MonitorsOverviewTabController } from '../../features/Overview/MonitorsOverviewTabController';
import type {
	MetricCardType,
	MonitorsPageTab,
} from '../../features/Overview/types';
import { useListPageTabNavigate } from '../../utils/hook/useTabNavigate';
import { useStyles } from '../TeamCatalogPage/TeamCatalogPage.styles';
import MonitorList from './MonitorList';

const METRIC_FILTERS: Partial<
	Record<
		MetricCardType,
		Array<{
			value: string;
			operator: string;
			filterType: string;
		}>
	>
> = {
	Normal: [
		{
			value: 'normal',
			operator: 'is',
			filterType: 'monitor_status',
		},
	],
	Error: [
		{
			value: 'error',
			operator: 'is',
			filterType: 'monitor_status',
		},
	],
	'Open Incidents': [
		{
			value: 'active',
			operator: 'is',
			filterType: 'incident_status',
		},
	],
	'Acknowledged Incidents': [
		{
			value: 'acknowledged',
			operator: 'is',
			filterType: 'incident_status',
		},
	],
	'Completed Sync': [
		{
			value: 'normal',
			operator: 'is',
			filterType: 'monitor_status',
		},
	],
	Failed: [
		{
			value: 'error',
			operator: 'is',
			filterType: 'monitor_status',
		},
	],
};

function MonitorsPage() {
	const { user, workspace } = useAuthUser();
	const { data: monitors, isLoading } = useMonitorList({});
	const navigateToTab = useListPageTabNavigate();
	const navigate = useNavigate();
	const { classes, theme } = useStyles();

	const [activeTab, setActiveTab] = useState<MonitorsPageTab>(() => {
		const storedTab = localStorage.getItem('monitors-tab');
		const urlParams = new URLSearchParams(window.location.search);
		// If there are filters in the URL, default to monitors tab
		if (urlParams.has('filters')) {
			return 'monitors';
		}
		// Otherwise use stored preference or default
		if (storedTab) {
			return storedTab as MonitorsPageTab;
		}
		return 'overview';
	});

	const handleMetricCardClick = useCallback(
		(metricType: MetricCardType) => {
			const filters = METRIC_FILTERS[metricType];
			if (!filters) return;

			navigate(
				`/monitors?filters=${encodeURIComponent(JSON.stringify(filters))}`
			);
			setActiveTab('monitors');
			trackEvent(
				'monitors/overview/metric_card/click',
				{
					metric_type: metricType,
				},
				user,
				workspace!
			);
		},
		[navigate, setActiveTab, user, workspace]
	);

	const handleTabChange = (value: string): void => {
		if (value === 'monitors' || value === 'incidents' || value === 'overview') {
			const tabValue = value as MonitorsPageTab;
			setActiveTab(tabValue);
			localStorage.setItem('monitors-tab', tabValue);
			// Only navigate if we're not already on the monitors page with filters
			if (
				!(value === 'monitors' && window.location.search.includes('filters'))
			) {
				navigateToTab(value);
			}
		}
		trackEvent(
			'monitors/tabs/click',
			{
				tab: value,
			},
			user,
			workspace!
		);
	};

	const emptyStateButtons = useMemo(
		() => [
			{
				label: 'Learn more',
				variant: 'default',
				onClick: () => {
					window.open(
						'https://docs.secoda.com/features/monitoring#how-to-create-monitors',
						'_blank'
					);
				},
			},
		],
		[]
	);

	const tabs = useMemo(
		() => [
			{ value: 'overview', label: 'Overview' },
			{ value: 'monitors', label: 'Monitors' },
		],
		[]
	);

	if (isLoading) {
		return <FullWidthLoadingSpinner />;
	}

	if (monitors && monitors.results.length === 0) {
		return (
			<EmptyState
				heading="Keep a pulse on your data"
				body="Add monitors to your data, set thresholds, and be alerted as soon as incidents are detected."
				illustrationName="monitors"
				buttons={emptyStateButtons as EmptyStateButtons}
			/>
		);
	}

	return (
		<FeatureGuard
			v1AllowedRoles={[
				UserRole.ADMIN,
				UserRole.EDITOR,
				UserRole.VIEWER,
				UserRole.GUEST,
			]}
			v2Permission="Monitors.Read"
			isFeatureIncludedInPlan={true}
			isAlwaysIncludedFeature={true}
			featureName="Monitors"
			description="Add monitors to your data, set thresholds, and be alerted as soon as incidents are detected."
		>
			<Stack className={classes.featureWrapper} spacing={0} pb="xl">
				<Tabs
					color="fill/brand/default"
					value={activeTab}
					onTabChange={handleTabChange}
					keepMounted={false}
				>
					<TabsList tabs={tabs} />
					<Tabs.Panel value="overview" pt="sm">
						<MonitorsOverviewTabController
							onMetricCardClick={handleMetricCardClick}
						/>
					</Tabs.Panel>
					<Tabs.Panel value="monitors" pt="sm">
						<MonitorList />
					</Tabs.Panel>
				</Tabs>
			</Stack>
		</FeatureGuard>
	);
}

export default MonitorsPage;
